import { __ } from '@wordpress/i18n'

import styled from '@emotion/styled'
import { useEffect, useRef, useState } from '@wordpress/element'
import { applyFilters } from "@wordpress/hooks"
import { FallbackBox } from '@wptravelengine/public/commonComponents'
import { PricingTable, Price } from '@wptravelengine/public/fragments'
import { keyBy } from 'lodash'
import { useQuery } from 'react-query'
import { decodeHTMLEntities } from './helper'

export const Counter = ({ min, max, value, onChange }) => {
    return <CounterWrapper className="wte-counter-wrap">
        <CounterButton className='wte-counter-btn wte-counter-btn-minus' disabled={value <= Number(min)} type="button" onClick={() => onChange(+value > Number(min) ? +value - 1 : Number(min))}>-</CounterButton>
        <input type="number"
            className='wte-counter-input'
            value={value === '' ? '' : value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => {
                const val = e.target.value;
                if (val === '' || val === null) {
                    onChange('');
                } else {
                    const numValue = parseInt(val);
                    onChange(isNaN(numValue) || numValue < 0 ? 0 : numValue);
                }
            }} />
        <CounterButton className='wte-counter-btn wte-counter-btn-plus' type="button" onClick={() => onChange(+value + 1)} disabled={value >= max}>+</CounterButton>
    </CounterWrapper>
}

const ServiceOptionDescription = ({ children }) => {
    const ref = useRef(null)
    const [expanded, setExpanded] = useState(false)
    const [isClamped, setIsClamped] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        setIsClamped(el.scrollHeight > el.clientHeight)
    }, [children])

    return <div className="wte-service-option-description-wrap">
        <div ref={ref} className={`wte-service-option-description ${expanded ? 'is-expanded' : ''}`}>
            {children}
        </div>
        {(isClamped || expanded) && (
            <button
                type="button"
                className="wte-service-option-description-toggle"
                onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}>
                {expanded ? __('Show less', 'wte-extra-services') : __('Show more', 'wte-extra-services')}
            </button>
        )}
    </div>
}

export const ServiceOptionItem = ({
    label,
    image = {},
    perTextLabel,
    description,
    afterLabel = "",
    price,
    isSoldOut = false,
    required,
    selectable,
    selected,
    onSelect,
    badges,
    badgeLabel,
    ...props }) => {
    const isSelected = Number(props.value) > 0
    return <div className={`wte-service-options-item ${selectable ? 'wte-service-options-item-selectable' : ''} ${isSelected ? 'wte-service-options-item-selected' : ''}`}>
        {image ? <div className="wte-service-option-image">
            {image?.url && <a href={image.fullUrl} data-fancybox={image.fullUrl} data-caption={image.alt || label}><img src={image.url} alt={image.alt || label} /></a>}
        </div> : null}
        <div className="wte-service-option-body">
            <div className="wte-service-option-title-wrap">
                <div className="wte-service-option-label">
                    <span dangerouslySetInnerHTML={{ __html: label + (required ? `<span class="required">*</span>` : '') }} />
                    {badgeLabel && <Badge text={badgeLabel} />}
                    {badges?.map((badge, index) => (
                        <span key={index} dangerouslySetInnerHTML={{ __html: badge }} />
                    ))}
                </div>
                {description && <ServiceOptionDescription>{description}</ServiceOptionDescription>}
            </div>
            <div className="wte-service-option-quantity-wrap">
                <div className="wte-service-option-pricing-wrap">
                    <Price value={price} />
                    <span className='wte-service-option-per-text'>{perTextLabel || ''}</span>
                </div>
                <div className='wte-service-option-select-wrapper' onClick={(e) => e.stopPropagation()}>
                    {!isSoldOut && <>
                        {(selectable && selected) || !selectable ? <Counter {...props} /> : null}
                        {selectable && <span onClick={() => selectable && onSelect()} role='switch' aria-checked={selected} className={`wte-service-option-selected-indicator ${selected ? 'selected' : ''}`}></span>}
                    </> || <Soldout>{__("Sold Out", "wte-extra-services")}</Soldout>}
                </div>
            </div>
        </div>
    </div>
}

const ServiceItem = ({ item, onChange, state }) => {
    const isOptSoldOut = (opt) => Number.isInteger(opt?.avaQty) && opt.avaQty === 0
    const [selectedOption, setSelectedOption] = useState(null)
    const { summary: { extraServices } } = state

    const { title, options, required, multiple, type, image, description, badgeLabel } = item

    const itemProps = { required }

    const extraServicesSummary = keyBy(extraServices, 'id')

    const getMax = (opt) => Number.isInteger(opt?.avaQty) ? opt.avaQty : Infinity

    const singleSelectable = !multiple && type === "custom"

    if (multiple || singleSelectable) {
        return options.map((option) => {
            const { price, label, key, title, badges } = option
            const defaultItem = <ServiceOptionItem
                key={key}
                {...{ label, price, direction: 'vertical' }}
                perTextLabel={`/ ${option.serviceUnit.label}`}
                image={option.image}
                description={option.description}
                min={singleSelectable ? 1 : 0}
                max={getMax(option)}
                value={extraServicesSummary[key]?.qty ?? 0}
                onChange={count => onChange(option, count)}
                onSelect={() => {
                    if (isOptSoldOut(options.find(o => o.key === key))) return
                    if (selectedOption === key) {
                        setSelectedOption(null)
                        onChange(option, 0)
                        return
                    }
                    setSelectedOption(key)
                    onChange({ key, price }, 1)
                }}
                selected={selectedOption === key || extraServicesSummary[key]?.qty > 0}
                selectable={singleSelectable}
                badges={badges}
            />
            return applyFilters(
                'wptravelengine.extraServices.optionItem',
                defaultItem,
                { option, item, isAdvancedCard: true }
            )
        })
    }

    const option = options.find(({ key }) => key === (selectedOption || (item.options.find(opt => !isOptSoldOut(opt)) || item.options[0])?.key))
    const displayLabel = options.length === 1 && options[0] ? options[0]?.label : (option?.title || title)

    const defaultItem = <ServiceOptionItem
        {...itemProps}
        perTextLabel={option ? `/ ${option?.serviceUnit?.label}` : ''}
        price={option?.price}
        image={option?.image}
        label={displayLabel}
        description={option?.description}
        min={0}
        max={getMax(option)}
        value={extraServicesSummary[option?.key]?.qty ?? 0}
        onChange={count => onChange(option, count)}
        badgeLabel={badgeLabel}
        badges={option?.badges} />

    return applyFilters(
        'wptravelengine.extraServices.optionItem',
        defaultItem,
        { option, item, isAdvancedCard: false }
    )
}

export const ServiceOptionsGroup = ({ title, required, chooseOne, badgeLabel, defaultExpanded = true, Box = ServiceOptionsBox, bodyClassName, children }) => {
    const [expanded, setExpanded] = useState(defaultExpanded)

    return <Box>
        <div
            className={`wte-service-multiple-options-header ${expanded ? 'is-expanded' : ''}`}
            onClick={() => setExpanded(prev => !prev)}
            role="button"
            aria-expanded={expanded}>
            <div className="wte-service-options-header-left">
                <h5 className="wte-service-options-title">
                    <span dangerouslySetInnerHTML={{ __html: title }} />
                    {required && <span className="wte-required" style={{ color: 'red' }}> *</span>}
                </h5>
                {badgeLabel && <Badge text={badgeLabel} />}
                <span className="wpte-tag success choose-one-tag"> {chooseOne ? __('Choose One', 'wte-extra-services') : __('Choose Any', 'wte-extra-services')} </span>
            </div>
            <span className={`wte-service-options-toggle-icon ${expanded ? 'is-expanded' : ''}`} aria-hidden="true" />
        </div>
        <div className={`wte-service-options-collapse ${expanded ? 'is-expanded' : ''}`}>
            <div className="wte-service-options-collapse-inner">
                <ServiceOptionsBody className={bodyClassName}>{children}</ServiceOptionsBody>
            </div>
        </div>
    </Box>
}

const withMultipleOptions = (Component) => ({ service, defaultExpanded, ...props }) => {
    const { multiple, id, title, required, type, badgeLabel } = service
    const chooseOne = !multiple && type === "custom"

    return multiple || (!multiple && type === "custom") ? (
        <ServiceOptionsGroup key={id} title={title} required={required} chooseOne={chooseOne} badgeLabel={badgeLabel} defaultExpanded={defaultExpanded}>
            <Component item={service} {...props} />
        </ServiceOptionsGroup>
    ) : <Component item={service} {...props} key={id} />
}

export default ({ state, setState }) => {
    const { tripID, summary: { extraServices, selectedTripDate } } = state
    const api = `${wpApiSettings.root}wptravelengine/v2/trips/${tripID}/services${selectedTripDate ? `?date=${selectedTripDate}` : ''}`
    const { data: services = [], isLoading } = useQuery(['extra-services', selectedTripDate], () => fetch(api).then(r => r.json()))

    const computeProcessToNext = (list) => {
        const requiredServiceIds = services.filter(s => s.required).map(s => s.id)
        return requiredServiceIds.every(sid => list.some(e => e.service_id === sid && e.qty > 0))
    }

    useEffect(() => {
        setState({
            processToNext: computeProcessToNext(extraServices),
            isLoading
        })
    }, [isLoading, services, extraServices])

    const handleChange = service => ({ key: _key, price, label }, count) => {
        const { options, id: service_id } = service
        const total = parseFloat(price) * count
        let tempServices = extraServices

        label || (tempServices = tempServices.filter(({ id }) => !service.options.some(({ key }) => key === id)))

        const serviceIndex = tempServices.findIndex(({ id }) => id === _key)
        if (serviceIndex !== -1) {
            tempServices[serviceIndex] = { ...tempServices[serviceIndex], qty: count, total }
        } else {
            const { price: unitPrice, serviceUnit, label } = options.find(({ key }) => key === _key)
            tempServices = [...tempServices, { id: _key, service_id, label, unitPrice, qty: count, total, perTextLabel: ` / ${serviceUnit.label}` }]
        }

        setState({
            processToNext: computeProcessToNext(tempServices),
            summary: { ...state.summary, extraServices: tempServices }
        })

    }

    const allSoldOut = Array.isArray(services) && services.length > 0 &&
        services.every(service => service.options?.every(opt => Number.isInteger(opt?.avaQty) && opt.avaQty === 0))

    // "Show Sold-out Service" comes from the Resource Management addon settings.
    // When disabled (and no filter override), hide sold-out services and show a notice instead.
    const showSoldOutServices = applyFilters('wptravelengine.extraServices.showAllSoldOut', !!(window.wpteRM?.settings?.extraServices?.showSoldOutService), { services })

    if (allSoldOut && !showSoldOutServices) {
        return (
            <div className="wte-process-tab-content-wrapper">
                <Fallback
                    title={__('Extra Services Sold Out', 'wte-extra-services')}
                    content={__('All extra services are currently sold out for this trip.', 'wte-extra-services')}
                />
            </div>
        )
    }

    const visibleServices = (Array.isArray(services) ? services : [])
        .map((service, index) => applyFilters('wptravelengine.extraServices.service', service, { index }))
        .filter(service => (service.options?.length ?? 0) > 0)
        .filter(service => applyFilters('wptravelengine.extraServices.renderService', true, { service }))

    if (!visibleServices.length) {
        return (
            <div className="wte-process-tab-content-wrapper">
                <Fallback
                    title={__('No Extra Services Available', 'wte-extra-services')}
                    content={__('There are no extra services available for this trip.', 'wte-extra-services')}
                />
            </div>
        )
    }

    return (
        <div className="wte-process-tab-content-wrapper">
            <h5 className="wte-process-tab-title">{wteL10n?.l10n?.extraServicesDescription || __('Select Extra Services', 'wte-extra-services')}</h5>
            {applyFilters('wptravelengine.tripBookingModal.extraServicesContent', <ExtraServicesContent>
                <PricingTable>
                    {() => visibleServices.map((service, index) => {
                        service = applyFilters(
                            'wptravelengine.extraServices.service',
                            service,
                            { index }
                        )
                        return withMultipleOptions(ServiceItem)({
                            service,
                            onChange: handleChange(service),
                            state,
                        })
                    })}
                </PricingTable>
            </ExtraServicesContent>, { services: visibleServices, onChange: handleChange, state, setState })}
        </div>
    )
}

const Badge = ({ text }) => {
    return <BadgeStyle className="wpte-tag">{text}</BadgeStyle>
}

export const ExtraServicesContent = styled.div`
    .wte-service-options-item{
        padding: 16px;
        border: 1px solid #efefef;
        border-radius: 8px;
        background-color: #fff;
        display: flex;
        flex-wrap: wrap;
        gap: 12px;

        &:not(:last-of-type){
            margin-bottom: 12px;
        }

        .wte-service-option-image{
            width: 80px;
            height: 80px;
            border-radius: 8px;
            background-color: rgba(var(--wte-primary-color-rgb, var(--primary-color-rgb)), 0.05);
            overflow: hidden;
            img{
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }
        
        .wte-service-option-body{
            display: flex;
            gap: 16px;
            flex: 1;
        }

        .wte-service-option-title-wrap{
            display: flex;
            flex-direction: column;
            gap: 4px;
            flex: 1;
        }

        .wte-service-option-label{
            font-size: 14px;
            font-weight: 500;
            line-height: 1.5;

            .wpte-tag{
                margin-left: 4px;
            }
        }

        .wte-service-option-description-wrap{
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
        }

        .wte-service-option-description{
            font-size: 12px;
            color: #7c7c7c;
            line-height: 1.5;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;

            &.is-expanded{
                -webkit-line-clamp: unset;
                overflow: visible;
            }
        }

        .wte-service-option-description-toggle{
            background: none;
            border: none;
            padding: 0;
            font-size: 12px;
            font-weight: normal;
            line-height: 1.5;
            color: var(--wpte-primary-color, var(--primary-color));
            cursor: pointer;
            text-decoration: underline;
        }

        .wte-service-option-quantity-wrap{
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-items: flex-end;
            min-width: 100px;

            .wte-service-option-pricing-wrap{
                .currency, .wpte-price{
                    font-weight: bold;
                }
            }
        }

        .wte-service-option-select-wrapper{
            display: inline-flex;
            align-items: center;
            gap: 8px;
            min-height: 26px;
        }

        .wte-service-option-selected-indicator{
            display: inline-block;
            width: 20px;
            height: 20px;
            border: 1px solid #cecece;
            border-radius: 50%;
            cursor: pointer;
            &:hover{
                border-color: var(--wpte-primary-color, var(--primary-color));
            }

            &.selected{
                background-color: var(--wpte-primary-color, var(--primary-color));
                border-color: var(--wpte-primary-color, var(--primary-color));
                background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 6L9 17L4 12' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
                background-repeat: no-repeat;
                background-size: 14px;
                background-position: center;
            }
        }

        .wte-service-option-pricing-wrap{
            display: flex;
            gap: 4px;
            align-items: center;
        }

        .wte-service-option-price{
            font-size: 16px;
            line-height: 1.5;
        }

        .wte-service-option-per-text{
            font-size: 12px;
            line-height: 1.5;
        }

        &.wte-service-options-item-selected{
            border-color: var(--wpte-primary-color, var(--primary-color));
            box-shadow: 0 2px 5px rgba(var(--wpte-primary-color-rgb, var(--primary-color-rgb)), 0.1), inset 3px 0 0 var(--wpte-primary-color, var(--primary-color));
            position: relative;
        }

        &.wte-service-options-item-selectable{
            transition: all .3s ease;
        }

        @media(max-width: 767px){
            align-items: flex-start;
            .wte-service-option-body{
                flex-direction: column;
                align-items: flex-start;
            }
            .wte-service-option-quantity-wrap{
                align-items: center;
                flex-flow: row wrap;
                justify-content: space-between;
                width: 100%;
            }
        }
    }
`

const Fallback = styled(FallbackBox)`
    background-color: rgba(var(--wpte-primary-color-rgb, var(--primary-color-rgb)), 0.1) !important;
    border: 1px solid rgba(var(--wpte-primary-color-rgb, var(--primary-color-rgb)), 0.5) !important;
`

export const ServiceOptionsBox = styled.div`
    border: 1px solid #efefef;
    border-radius: 8px;
    margin-bottom: 12px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .wte-service-multiple-options-header{
        padding: 12px 16px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        user-select: none;
        background-color: #f7f7f7;

        &.is-expanded{
            border-bottom: 1px solid #efefef;
        }

        .wte-service-options-header-left{
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 8px;
            flex: 1;
        }

        .choose-one-tag{
            background-color: rgba(var(--wpte-primary-color-rgb, var(--primary-color-rgb)), 0.05);
            border-color: rgba(var(--wpte-primary-color-rgb, var(--primary-color-rgb)), 0.5);
            color: var(--wpte-primary-color, var(--primary-color));
            margin-left: auto;
        }

        .wte-service-options-toggle-icon{
            flex: 0 0 auto;
            width: 8px;
            height: 8px;
            border-right: 2px solid #6b6b6b;
            border-bottom: 2px solid #6b6b6b;
            transform: rotate(45deg);
            transition: transform .3s ease, margin .3s ease;
            margin-bottom: 3px;

            &.is-expanded{
                transform: rotate(-135deg);
                margin-bottom: -3px;
            }
        }
    }

    .wte-service-options-title{
        font-size: 16px;
        font-weight: 600;
        margin: 0;
        line-height: 1.45;
    }

    .wte-service-options-collapse{
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows .3s ease;

        &.is-expanded{
            grid-template-rows: 1fr;
        }
    }

    .wte-service-options-collapse-inner{
        overflow: hidden;
        min-height: 0;
    }
`

export const ServiceOptionsBody = styled.div`
    display: flex;
    flex-direction: column;

    .wte-service-options-item{
        border: none;
        border-radius: 0;
        margin: 0 !important;
        &:not(:last-of-type){
            border-bottom: 1px solid #efefef !important;
        }
    }
`

const BadgeStyle = styled.span`
    background-color: #f59e0b;
    border-color: #f59e0b;
    color: #fff;
`

const CounterWrapper = styled.div`
    display: inline-flex;
    align-items: center;

    input[type="number"]{
        padding: 0;
        border: none;
        width: 35px;
        text-align: center;
        font-size: 14px;
        min-height: unset;
        font-weight: bold;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    }

    /* Target Mozilla Firefox */
    input[type=number] {
    -moz-appearance: textfield;
    appearance: textfield;
    }
`

export const CounterButton = styled.button`
    flex: 0 0 26px;
    width: 26px;
    height: 26px;
    background: #ffffff;
    border: 1px solid #b0b0b0;
    border-radius: 50%;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #999;
    font-weight: 600;
    &:hover{
        color: var(--wpte-primary-color, var(--primary-color));
        border-color: var(--wpte-primary-color, var(--primary-color));
        background: none;
    }
`

const Soldout = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #f65557;
    font-weight: 500;
    white-space: nowrap;
`;